// components/modals/MeetingsList.jsx
// Every meeting with a transcript in one list: found in Teams or uploaded
// (and sessions of series added before uploads existed).

import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FiAlertTriangle,
  FiEye,
  FiLoader,
  FiRefreshCw,
  FiTrash2,
  FiUser,
  FiUsers,
} from 'react-icons/fi';

import MeetingDetailModal from '../meeting/MeetingDetailModal';
import * as meetingsApi from '../../services/meetingsApi';
import { confirmDialog } from '../ConfirmDialog';
import { personLabel } from '../project-register/shared.jsx';

const STATUS = {
  pending: ['Waiting to start', 'text-blue-400 border-blue-900/50'],
  queued: ['Queued', 'text-cyan-400 border-cyan-900/50'],
  processing: ['Processing', 'text-amber-400 border-amber-900/50'],
  reprocessing: ['Reprocessing', 'text-orange-400 border-orange-900/50'],
  processed: ['Processed', 'text-emerald-400 border-emerald-900/50'],
  no_transcript: ['No transcript', 'text-zinc-400 border-zinc-700'],
  failed: ['Failed', 'text-red-400 border-red-900/50'],
  tenant_setting_off: [
    'Blocked by Teams setting',
    'text-red-400 border-red-900/50',
  ],
};

// Sessions of series added before uploads existed read as uploads too.
const SOURCE = { teams: 'Teams', upload: 'Uploaded', manual: 'Uploaded' };

// What a Teams admin must switch on, and where.
const ADMIN_PATH =
  'Teams admin center → Meetings → Meeting settings → Transcript API access';
export const TENANT_SETTINGS = {
  microsoft_graph_access: {
    name: 'Microsoft Graph access',
    path: `${ADMIN_PATH} → Microsoft Graph access`,
  },
  include_speaker_attribution: {
    name: 'Include speaker attribution',
    path: `${ADMIN_PATH} → Configure → Include speaker attribution`,
  },
};

const formatStart = (row) =>
  row.date_only
    ? new Date(`${row.start.slice(0, 10)}T00:00:00`).toLocaleDateString(
        undefined,
        { dateStyle: 'medium' },
      )
    : new Date(row.start).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });

const time = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { timeStyle: 'short' });

const minutes = (row) =>
  row.duration ||
  (row.end && Math.round((new Date(row.end) - new Date(row.start)) / 60000));

const name = (p) => p.name || personLabel(p.email);

const chip =
  'flex min-w-0 items-center gap-1.5 rounded border border-zinc-800/50 bg-zinc-900/50 px-2 py-1 text-[10px] text-zinc-400';

// "19 Oct 2026, 10:55 – 11:25 · 30 min": the end and length when known.
const formatWhen = (row) => {
  const length = minutes(row);
  return [
    formatStart(row) +
      (length && row.end && !row.date_only ? ` – ${time(row.end)}` : ''),
    length ? `${length} min` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

// Who organised it and who was there.
const Details = ({ row }) => {
  const people = row.attendees || [];
  if (!row.organizer && !people.length) return null;
  const shown = people.slice(0, 3).map(name).join(', ');
  const more = people.length > 3 ? ` +${people.length - 3}` : '';
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {row.organizer && (
        <span
          className={chip}
          title={`Organiser: ${row.organizer.email || ''}`}
        >
          <FiUser className="h-3 w-3 shrink-0 text-zinc-500" />
          <span className="truncate">{name(row.organizer)}</span>
        </span>
      )}
      {people.length > 0 && (
        <span
          className={chip}
          title={people.map((p) => p.email || name(p)).join('\n')}
        >
          <FiUsers className="h-3 w-3 shrink-0 text-zinc-500" />
          <span className="truncate">
            {shown}
            {more}
          </span>
        </span>
      )}
    </div>
  );
};

const canRetry = (row) =>
  row.kind === 'teams'
    ? // No transcript: one cannot be added after the meeting, so no retry.
      ['failed', 'tenant_setting_off', 'processed'].includes(row.status)
    : ['pending', 'failed', 'processed'].includes(row.status);

const MeetingsList = ({
  accessToken,
  canManage,
  searchQuery,
  kind = null,
  refreshKey,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await meetingsApi.listAllMeetings(accessToken));
    } catch (err) {
      toast.error(`Failed to load meetings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // While something is being processed, check again so its status updates.
  const working = rows.some((r) =>
    ['queued', 'processing', 'reprocessing'].includes(r.status),
  );
  useEffect(() => {
    if (!working) return undefined;
    const timer = setTimeout(load, 10000);
    return () => clearTimeout(timer);
  }, [working, rows, load]);

  const act = async (row, action) => {
    setBusyId(row.id);
    try {
      await action();
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const retry = (row) =>
    act(row, async () => {
      if (row.kind === 'teams')
        await meetingsApi.retryTeamsMeeting(accessToken, row.id);
      else
        await meetingsApi.processMeetingEvent(
          accessToken,
          row.meeting_id,
          row.id,
        );
      toast.success('Queued for processing.');
    });

  const remove = async (row) => {
    const teams = row.kind === 'teams';
    const ok = await confirmDialog({
      title: `Remove "${row.title}"?`,
      message: teams
        ? 'It is hidden from this list and not processed again. The meeting stays in Teams.'
        : row.source === 'manual'
          ? 'The meeting, every session of it and their results are deleted. This cannot be undone.'
          : 'The uploaded transcript and its results are deleted. This cannot be undone.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    act(row, async () => {
      if (row.kind === 'teams')
        await meetingsApi.hideTeamsMeeting(accessToken, row.id);
      else await meetingsApi.deleteMeeting(accessToken, row.meeting_id);
      toast.success('Meeting removed.');
    });
  };

  const query = (searchQuery || '').toLowerCase();
  const shown = rows.filter(
    (r) => (!kind || r.kind === kind) && r.title.toLowerCase().includes(query),
  );

  if (loading && !rows.length)
    return (
      <div className="flex h-32 items-center justify-center">
        <FiLoader className="animate-spin text-xl text-[#5652CF]" />
      </div>
    );
  if (!shown.length)
    return (
      <p className="py-12 text-center text-xs text-zinc-500">
        {kind === 'manual'
          ? `No uploaded meetings${canManage ? '. Use Upload transcript to add one.' : '.'}`
          : `No meetings yet. Teams meetings with a transcript appear here by themselves${canManage && !kind ? '; you can also upload a transcript.' : '.'}`}
      </p>
    );

  return (
    <>
      {detail && (
        <MeetingDetailModal
          eventId={detail.id}
          isManual={detail.kind === 'manual'}
          onClose={() => setDetail(null)}
        />
      )}
      <ul className="space-y-2" aria-label="Meetings">
        {shown.map((row) => {
          const [label, style] = STATUS[row.status] || [row.status, ''];
          const setting = TENANT_SETTINGS[row.tenant_setting];
          return (
            <li
              key={`${row.kind}:${row.id}`}
              className="rounded-md border border-zinc-800 bg-zinc-800/40 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-xs font-bold text-zinc-200">
                    {row.title}
                  </h3>
                  <p className="mt-0.5 text-[10px] text-zinc-500">
                    {SOURCE[row.source] || row.source} · {formatWhen(row)}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${style}`}
                >
                  {label}
                </span>
              </div>
              <Details row={row} />

              {row.status === 'tenant_setting_off' && (
                <div className="mt-2 flex gap-2 rounded border border-red-900/50 bg-red-950/20 p-2 text-[11px] text-zinc-300">
                  <FiAlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />
                  <div>
                    <p>
                      Your Teams admin has switched off{' '}
                      <strong>
                        {setting?.name || 'Transcript API access'}
                      </strong>
                      , so this meeting's transcript cannot be read. Ask them to
                      turn it on in:
                    </p>
                    <p className="mt-1 font-mono text-[10px] text-zinc-400">
                      {setting?.path || ADMIN_PATH}
                    </p>
                    <p className="mt-1 text-zinc-500">
                      {canManage
                        ? 'Then press Retry.'
                        : 'Then someone who manages meetings can retry it.'}
                    </p>
                  </div>
                </div>
              )}
              {row.status === 'no_transcript' && (
                <p className="mt-2 text-[11px] text-zinc-400">
                  Transcription was not started in this meeting, so there is
                  nothing to process. Next time, start it in Teams (More →
                  Record and transcribe → Start transcription), or ask your
                  Teams admin to turn it on automatically.
                </p>
              )}
              {row.status === 'failed' && (
                <div className="mt-2 text-[11px] text-red-400/90">
                  Processing failed.{' '}
                  {canManage
                    ? 'Press Retry to try again.'
                    : 'Someone who manages meetings can retry it.'}
                  {row.error && (
                    <details className="mt-1 text-[10px] text-zinc-500">
                      <summary className="cursor-pointer">Details</summary>
                      <p className="mt-1 break-words">{row.error}</p>
                    </details>
                  )}
                </div>
              )}

              <div className="mt-2 flex justify-end gap-1">
                {row.status === 'processed' && (
                  <button
                    type="button"
                    onClick={() => setDetail(row)}
                    className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-zinc-300 hover:bg-zinc-700"
                  >
                    <FiEye className="h-3 w-3" /> Open
                  </button>
                )}
                {canManage && canRetry(row) && (
                  <button
                    type="button"
                    onClick={() => retry(row)}
                    disabled={busyId === row.id}
                    className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
                  >
                    <FiRefreshCw className="h-3 w-3" />
                    {{ pending: 'Process', processed: 'Reprocess' }[
                      row.status
                    ] || 'Retry'}
                  </button>
                )}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => remove(row)}
                    disabled={busyId === row.id}
                    aria-label={`Remove ${row.title}`}
                    className="rounded p-1 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                  >
                    <FiTrash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default MeetingsList;
